import { useMemo } from 'react'

import { useCartMonitor } from './useCartMonitor'
import { usePromoData } from './usePromoData'
import { DISCOUNTS, PROMOTION_LEVELS, getDiscountLabel } from '../../lib/constants'
import { mapPromoProductToProduct } from '../../lib/mappers'

import type { DiscountFactors, DiscountLevel, GraphQLProduct, PromoContextValue, PromoProduct } from '../../types'


/**
 * Compute discount factors for each child category based on level.
 *
 * Level 1 (total < 1000): first=70% off (0.3), second not shown
 * Level 2 (total >= 1000): first=90% off (0.1), second=50% off (0.5)
 */
function getDiscountFactors(level: DiscountLevel): DiscountFactors {
  if (level === 1) {
    return { firstChildFactor: DISCOUNTS[2], secondChildFactor: DISCOUNTS[1] }
  }
  return { firstChildFactor: DISCOUNTS[3], secondChildFactor: DISCOUNTS[1] }
}

/**
 * Map GraphQL products to PromoProducts with discount applied.
 */
function mapToPromoProducts(
  items: GraphQLProduct[],
  factor: number
): PromoProduct[] {
  const label = getDiscountLabel(factor)
  return items.map((item) => ({
    product: mapPromoProductToProduct(item, factor),
    discountLabel: label,
  }))
}

/**
 * Composition hook combining usePromoData + useCartMonitor.
 *
 * Pre-computes mapped products with discount prices and labels.
 */
export function usePromoState(): PromoContextValue {
  const { blockData, firstChildCategoryItems, secondChildCategoryItems, loading, error } =
    usePromoData()
  const { total, level } = useCartMonitor()

  const { firstChildFactor, secondChildFactor } = useMemo(
    () => getDiscountFactors(level),
    [level]
  )

  const firstCategoryProducts = useMemo(
    () => mapToPromoProducts(firstChildCategoryItems, firstChildFactor),
    [firstChildCategoryItems, firstChildFactor]
  )

  const secondCategoryProducts = useMemo(
    () => mapToPromoProducts(secondChildCategoryItems, secondChildFactor),
    [secondChildCategoryItems, secondChildFactor]
  )

  return {
    blockData,
    loading,
    error,
    total,
    level,
    firstCategoryProducts,
    secondCategoryProducts,
    promotionLevels: PROMOTION_LEVELS,
  }
}
